import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useApi } from '../../lib/api';
import { parseStreamFrame } from '../../lib/parseStreamFrame';
import { createAttemptTracker, type AttemptTracker } from '../../lib/stream-attempts';
import {
  type StreamMessage,
  type UserMessage,
  type AssistantMessage as AssistantMsg,
} from '../../types/stream-protocol';

// Error state type

export type ErrorState =
  | { kind: 'deleted' }
  | { kind: 'session-expired' }
  | { kind: 'unavailable' }
  /** Backoff reconnect in flight. FlowChatSessionsProvider's completion-toast
   * qualifier reads `error === null`, so this must be non-null during an outage. */
  | { kind: 'reconnecting' }
  | null;

// Return type

export interface UseConversationStreamReturn {
  messages: StreamMessage[];
  isRunning: boolean;
  error: ErrorState;
  send: (question: string) => Promise<void>;
  /**
   * True once the `'replay-done'` control event has fired for the current
   * conversation — additive (U3, Flow multi-session): `isRunning` toggles
   * per REPLAYED message during initial history playback (lines below,
   * `es.onmessage`), which is not a real run boundary. A caller that treats
   * an `isRunning` true→false edge as "a run finished" (e.g. to bump a
   * session's `lastActivityAt`) must gate on `replayDone` first. Existing
   * callers (V4's `KeystoneChatSurface`, DSRP) are unaffected — this is a
   * new field on an existing return object, nothing else changed. Optional
   * (rather than required) so existing typed literals that construct a
   * `UseConversationStreamReturn` without it (e.g. test doubles for
   * `externalStream`) keep compiling — additive-only per
   * coding-standards.md §1.
   */
  replayDone?: boolean;
  /**
   * `outcome` of the most recent LIVE `turn_done` ('done' | 'failed' | ...),
   * `null` before one, after a new `user` frame or `send`, and for replayed or
   * suppressed turns. Optional for the same reason as `replayDone`. A failed
   * turn that streamed deltas leaves a partial `assistant` message last, so the
   * last message type cannot tell success from failure.
   */
  turnOutcome?: string | null;
}

// Hook

export function useConversationStream(
  conversationId: string | null,
  options?: {
    onConversationDeleted?: () => void;
    /**
     * Flow multi-session stop-then-ask suppression (Wave 2 review finding
     * #3) — additive, default `undefined`/no-op for the V4 caller. Real
     * server-side cancellation isn't possible (Keystone owns generation;
     * `docs/flow/keystone-conversation-cancel-handoff.md`), so a Stopped
     * session that's resumed with a NEW question can still have its OLD,
     * pre-stop question answered by the backend after the new one is sent —
     * arriving on this same stream, appended at the end of `messages`
     * regardless of true chronology (the new question's own optimistic
     * append already happened by the time the stale answer shows up).
     *
     * There is no field on `AssistantMessage` correlating an answer back to
     * the `UserMessage` it answers, so this can't key off a shared id on
     * the ANSWER — only on the ASK: `suppressAnswerForMessageId` is the
     * `messageId` of the old, unanswered `UserMessage` that was in flight
     * at Stop. Once a `'user'` message with that exact id is observed
     * (during replay or live — the caller arms this once per mount, from
     * whatever was captured at Stop time), the very next `'assistant'` or
     * `'error'` finalization is treated as the answer to THAT ask and is
     * dropped entirely: never appended to `messages`, and `isRunning` is
     * left untouched (so a stale finalize can't falsely signal "done" while
     * the real new answer is still pending). Exactly one drop is armed —
     * ordering guarantees it's safe (a conversation processes asks
     * serially, so the stale answer always surfaces before the new ask's
     * own answer).
     */
    suppressAnswerForMessageId?: string | null;
  },
): UseConversationStreamReturn {
  const { apiFetch } = useApi();
  const { getToken } = useAuth();

  // ---- State ----
  const [messages, setMessages] = useState<StreamMessage[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [errorState, setErrorState] = useState<ErrorState>(null);
  const [replayDone, setReplayDone] = useState(false);
  const [turnOutcome, setTurnOutcome] = useState<string | null>(null);

  // ---- Refs ----
  const eventSourceRef = useRef<EventSource | null>(null);
  const deltaInProgress = useRef<Set<string>>(new Set());
  const replayMode = useRef(true);

  // Stop-then-ask suppression (see options doc above). `armFor` is captured
  // fresh at the top of every connect (reset alongside the other per-
  // connect refs below), never re-armed mid-connection from a later render
  // — a caller wanting a NEW suppression target must remount (Flow's
  // `SessionStreamHost` does exactly this on resume, via `bumpHost`).
  // `active` flips true once the armed ask's `messageId` is actually
  // observed on the wire, and flips back false the moment its one
  // suppressed answer has been consumed.
  const suppressArmedForRef = useRef<string | null>(null);
  const suppressActiveRef = useRef(false);

  // Dedupe and Keystone retries (`turn_superseded`) go through the attempt
  // tracker (`lib/stream-attempts.ts`): it hides a superseded attempt's ids,
  // drops its late frames, keeps `user` frames visible, and holds
  // `retryPending` until the retry finalizes. `suppressConsumedId` is the
  // messageId of the answer stop-then-ask suppression swallowed; if a retry
  // later supersedes it, suppression re-arms for the retry's answer.
  const tracker = useRef<AttemptTracker>(createAttemptTracker());
  const suppressConsumedId = useRef<string | null>(null);
  // `turn_done` is best-effort and never appended, so replay-done cannot infer
  // "finished" from the last message. True from a turn_done until the next
  // `user` frame; `suppressedTurnId` is the turn whose answer suppression ate.
  const turnEnded = useRef(false);
  const suppressedTurnId = useRef<string | null>(null);

  // Stable ref for the onConversationDeleted callback
  const onConversationDeletedRef = useRef(options?.onConversationDeleted);
  onConversationDeletedRef.current = options?.onConversationDeleted;

  // ---- EventSource lifecycle ----
  useEffect(() => {
    if (!conversationId) return;

    // Reset state for new conversation
    setMessages([]);
    setIsRunning(false);
    setErrorState(null);
    setReplayDone(false);
    setTurnOutcome(null);
    tracker.current = createAttemptTracker();
    deltaInProgress.current.clear();
    replayMode.current = true;
    suppressArmedForRef.current = options?.suppressAnswerForMessageId ?? null;
    suppressActiveRef.current = false;
    suppressConsumedId.current = null;
    turnEnded.current = false;
    suppressedTurnId.current = null;

    let cancelled = false;
    let authRetries = 0;
    const MAX_AUTH_RETRIES = 3;
    // Last SSE `id:` (Keystone emits one per message) lets a reconnect resume via fromOffset.
    let lastEventId: string | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffAttempt = 0;
    let backoffStart: number | null = null;

    async function connect() {
      // Keystone replays from fromOffset on reconnect; skip deltas as on first connect.
      replayMode.current = true;

      let url = `/api/keystone/conversations/${conversationId}/stream`;
      const params = new URLSearchParams();
      if (lastEventId) {
        params.set('fromOffset', lastEventId);
      }
      try {
        const token = await getToken();
        if (token) params.set('__session', token);
      } catch { /* proceed without token — cookies may still work */ }
      const qs = params.toString();
      if (qs) url += `?${qs}`;

      if (cancelled) return;

      const es = new EventSource(url);
      eventSourceRef.current = es;

      // A successful connection is the one place backoff state resets — a
      // quiet reconnect (no messages, just replay-done) still needs the NEXT
      // outage to start its own backoff instead of inheriting this one's.
      es.onopen = () => {
        backoffAttempt = 0;
        backoffStart = null;
        setErrorState((prev) => (prev?.kind === 'reconnecting' ? null : prev));
      };

      // --- message event (default SSE event) ---
      es.onmessage = (event: MessageEvent) => {
        // Successful data means auth is valid — reset retry counter
        authRetries = 0;
        if (event.lastEventId) {
          lastEventId = event.lastEventId;
        }
        try {
          const parsed = parseStreamFrame(event.data, 'useConversationStream');
          if (!parsed) return;

          const a = tracker.current.admit(parsed);
          if (a.kind === 'skip') return;
          if (a.kind === 'supersede') {
            for (const id of a.removedIds) deltaInProgress.current.delete(id);
            if (suppressConsumedId.current !== null && a.removedIds.has(suppressConsumedId.current)) {
              suppressActiveRef.current = true;
              suppressConsumedId.current = null;
            }
            setMessages((prev: StreamMessage[]) => prev.filter((m) => !a.removedIds.has(m.messageId)));
            setIsRunning(true);
            return;
          }
          const msg = a.frame;

          // Handle assistant_delta messages specially
          if (msg.type === 'assistant_delta') {
            const delta = msg;

            // During replay, skip deltas (the final assistant message will follow)
            if (replayMode.current) return;

            // Deltas for the armed-and-suppressed stale answer — drop
            // silently (see options doc above). Only one generation can be
            // in flight per conversation at a time, so while suppression is
            // active every delta belongs to the stale answer by definition.
            if (suppressActiveRef.current) return;

            // Mark this message as being accumulated via deltas
            deltaInProgress.current.add(delta.parentMessageId);

            // Upsert a synthetic in-progress AssistantMessage.
            setMessages((prevMsgs: StreamMessage[]) => {
              const existingIdx = prevMsgs.findIndex(
                (m) => m.messageId === delta.parentMessageId,
              );

              const existingMsg = existingIdx >= 0 ? prevMsgs[existingIdx] : undefined;
              const prevContent =
                existingMsg?.type === 'assistant'
                  ? existingMsg.content
                  : '';
              const accumulated = prevContent + delta.delta;

              // Remove the most recent status message when first delta arrives
              const isFirstDelta = existingIdx < 0;
              let filtered = prevMsgs;
              if (isFirstDelta) {
                let lastStatusIdx = -1;
                for (let i = prevMsgs.length - 1; i >= 0; i--) {
                  if (prevMsgs[i].type === 'status') {
                    lastStatusIdx = i;
                    break;
                  }
                }
                if (lastStatusIdx >= 0) {
                  filtered = [
                    ...prevMsgs.slice(0, lastStatusIdx),
                    ...prevMsgs.slice(lastStatusIdx + 1),
                  ];
                }
              }

              // Build synthetic assistant message (no stats -> convertMessage marks as running)
              const syntheticMsg: AssistantMsg = {
                type: 'assistant',
                messageId: delta.parentMessageId,
                conversationId: delta.conversationId,
                timestamp: delta.timestamp,
                content: accumulated,
                accountId: '',
                accountName: '',
                turnId: delta.turnId,
                attempt: delta.attempt,
              };

              if (isFirstDelta) {
                return [...filtered, syntheticMsg];
              }
              const updated = [...filtered];
              updated[existingIdx] = syntheticMsg;
              return updated;
            });

            return;
          }

          if (msg.type === 'turn_done') {
            // The stale turn's end must not read as the new ask finishing.
            if (suppressActiveRef.current) return;
            if (msg.turnId !== undefined && msg.turnId === suppressedTurnId.current) return;
            turnEnded.current = true;
            if (!replayMode.current) setTurnOutcome(msg.outcome);
            setIsRunning(false);
            return;
          }

          // The stale answer (or its error) finalizing — discard entirely:
          // never appended, `isRunning` left untouched (see options doc
          // above for why). Consumes the one-shot suppression.
          if (suppressActiveRef.current && (msg.type === 'assistant' || msg.type === 'error')) {
            deltaInProgress.current.delete(msg.messageId);
            suppressActiveRef.current = false;
            suppressConsumedId.current = msg.messageId;
            suppressedTurnId.current = msg.turnId ?? null;
            return;
          }

          // When final assistant message arrives, clean up delta tracking
          if (msg.type === 'assistant') {
            const parentId = msg.messageId;
            if (deltaInProgress.current.has(parentId)) {
              deltaInProgress.current.delete(parentId);
              setMessages((prevMsgs: StreamMessage[]) => {
                const idx = prevMsgs.findIndex(
                  (m) => m.messageId === parentId,
                );
                if (idx >= 0) {
                  const updated = [...prevMsgs];
                  updated[idx] = msg;
                  return updated;
                }
                return [...prevMsgs, msg];
              });
              setIsRunning(false);
              return;
            }
          }

          setMessages((prevMsgs: StreamMessage[]) => [...prevMsgs, msg]);

          // Infer isRunning from message type
          if (msg.type === 'assistant' || msg.type === 'error') {
            setIsRunning(false);
          } else if (msg.type === 'user') {
            turnEnded.current = false;
            setTurnOutcome(null);
            setIsRunning(true);
            // Arm suppression once we've actually observed the stale ask on
            // the wire (during replay or live) — the ask itself is a real,
            // legitimate part of history and stays visible; only ITS answer
            // gets dropped, one time.
            if (suppressArmedForRef.current !== null && msg.messageId === suppressArmedForRef.current) {
              suppressActiveRef.current = true;
              suppressArmedForRef.current = null;
            }
          }
        } catch {
          // Ignore malformed SSE data
        }
      };

      // --- replay-done control event ---
      es.addEventListener('replay-done', () => {
        authRetries = 0;
        replayMode.current = false;
        setReplayDone(true);
        setMessages((prev: StreamMessage[]) => {
          if (prev.length === 0) {
            setIsRunning(tracker.current.retryPending);
            return prev;
          }
          const last = prev[prev.length - 1];
          setIsRunning((last.type === 'user' && !turnEnded.current) || tracker.current.retryPending);
          return prev;
        });
      });

      // --- error handling ---
      es.onerror = () => {
        if (es.readyState === EventSource.CLOSED) {
          void probeStreamEndpoint(url).then((status) => {
            // Wave 2 review finding — only the 401 branch checked
            // `cancelled`; the 404/503 branches ran unconditionally and
            // could fire after this host/session has already been
            // stopped/unmounted (a stop closes the EventSource, but this
            // probe's own `fetch()` keeps running independently and can
            // resolve well after `cancelled` flips true), mutating state
            // for a session nothing is listening to anymore via
            // `onConversationDeletedRef` → `store.markDeleted`. Gate ALL
            // branches on the same flag the 401 branch already used.
            if (cancelled) return;
            if (status === 404) {
              setErrorState({ kind: 'deleted' });
              onConversationDeletedRef.current?.();
            } else if (status === 401) {
              if (authRetries < MAX_AUTH_RETRIES) {
                authRetries++;
                es.close();
                void connect();
              } else {
                setErrorState({ kind: 'session-expired' });
              }
            } else if (status === 502 || status === 503 || status === 504 || status === 0) {
              // 0 is probeStreamEndpoint's sentinel for a fetch that threw.
              scheduleReconnect();
            }
          });
        }
      };
    }

    // Keystone deploys are a hard stop-then-start (persistent disk disables
    // Render zero-downtime), so 502s last one to two minutes.
    function scheduleReconnect() {
      if (backoffStart === null) {
        backoffStart = Date.now();
      }
      const elapsed = Date.now() - backoffStart;
      if (elapsed >= RECONNECT_GIVE_UP_MS) {
        setErrorState({ kind: 'unavailable' });
        backoffAttempt = 0;
        backoffStart = null;
        return;
      }
      setErrorState({ kind: 'reconnecting' });
      const delay = nextReconnectDelay(backoffAttempt);
      backoffAttempt++;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (cancelled) return;
        void connect();
      }, delay);
    }

    void connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
    // `options.suppressAnswerForMessageId` is deliberately NOT a dependency —
    // it must be captured once per CONNECT (the top-of-effect reset above),
    // never re-armed by a later render of the same mount that happens to
    // pass a new value; a caller wanting a fresh value must remount (Flow's
    // `SessionStreamHost` does this via `bumpHost` on resume). `options`
    // itself isn't referentially stable across renders either, so adding it
    // would defeat the connect-once semantics the rest of this effect relies
    // on (same treatment as `onConversationDeletedRef` below, which reads
    // its own ref rather than a dep for the same reason).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, getToken]);

  // ---- send function ----
  const send = useCallback(
    async (question: string) => {
      if (!conversationId) return;
      if (!question.trim()) return;

      const clientMessageId = crypto.randomUUID();

      // Build optimistic user message
      const optimisticMsg: UserMessage = {
        type: 'user',
        messageId: clientMessageId,
        conversationId,
        timestamp: new Date().toISOString(),
        content: question,
      };

      // Add to local state immediately; the server echo then dedupes
      tracker.current.markSeen(clientMessageId);
      turnEnded.current = false;
      setTurnOutcome(null);
      setMessages((prev: StreamMessage[]) => [...prev, optimisticMsg]);
      setIsRunning(true);

      // POST to backend
      try {
        await apiFetch(
          `/api/keystone/conversations/${conversationId}/ask`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ question, clientMessageId }),
          },
        );
      } catch {
        // The SSE stream will deliver the server's response (or error).
      }
    },
    [conversationId, apiFetch],
  );

  return { messages, isRunning, error: errorState, send, replayDone, turnOutcome };
}

// Helpers

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 15_000;
const RECONNECT_GIVE_UP_MS = 3 * 60 * 1000;

function nextReconnectDelay(attempt: number): number {
  const base = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);
  const jitter = Math.random() * base * 0.2;
  return base + jitter;
}

/**
 * Probe the stream endpoint to determine the HTTP status code.
 * EventSource doesn't expose status codes, so we do a lightweight fetch.
 */
async function probeStreamEndpoint(url: string): Promise<number> {
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'text/event-stream' },
    });
    void res.body?.cancel();
    return res.status;
  } catch {
    return 0;
  }
}
